// src/pages/account/ResetPassword.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './ResetPassword.css';
import { NotificationContext } from '../../context/NotificationContext';
import { BASE_URL } from '../../utils/config';
import { validatePassword } from '../../utils/userValidation';
import { useLanguage } from '../../hooks/useLanguage';

const ResetPassword = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useContext(NotificationContext);
  const lang = useLanguage();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(null); // null: checking, true: valid, false: invalid

  useEffect(() => {
    // TODO: Optionally, you could add a backend call here to verify the token
    // as soon as the page loads, before the user even tries to submit a new password.
    // For now, we'll assume the token is valid until submission.
    if (!token) {
      addNotification(t('resetPasswordPage.tokenMissing'), 'error');
      setTokenValid(false);
      // navigate('/login'); // Or to a specific error page
    } else {
      setTokenValid(true); // Assume valid for now
    }
  }, [token, addNotification, navigate, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      const errorMsg = t('resetPasswordPage.passwordsDoNotMatch');
      setError(errorMsg);
      addNotification(errorMsg, 'error');
      return;
    }
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      addNotification(passwordError, 'error');
      return;
    }

    setIsSubmitting(true);
    addNotification(t('resetPasswordPage.resetting'), 'info');

    try {
      const response = await fetch(`${BASE_URL}/authUser/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await response.json();

      if (response.ok) {
        addNotification(t('resetPasswordPage.success'), 'success', 7000);
        navigate(`/${lang}/login`);
      } else {
        // Use the message from the backend if available, otherwise a generic one.
        // The backend sends 400 for invalid/expired token, and 500 for server errors.
        // The message from backend for 400 is "Password reset token is invalid or has expired."
        addNotification(data.message || t('resetPasswordPage.failure'), 'error', 7000);
      }
    } catch (err) {
      console.error('Reset password error:', err);
      addNotification(t('resetPasswordPage.genericError'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (tokenValid === false) {
    return (
      <div className="reset-password-page-container">
        <div className="reset-password-container">
          <h2>{t('resetPasswordPage.invalidLinkTitle')}</h2>
          <p>{t('resetPasswordPage.invalidLinkMessage')}</p>
          <Link to={`/${lang}/forgot-password`}>{t('resetPasswordPage.requestNewLink')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page-container">
      <div className="reset-password-container">
        <h2>{t('resetPasswordPage.title')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">{t('resetPasswordPage.newPasswordLabel')}</label>
            <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={isSubmitting} autoComplete="new-password" />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">{t('resetPasswordPage.confirmPasswordLabel')}</label>
            <input type="password" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required disabled={isSubmitting} autoComplete="new-password" />
          </div>
          {error && <p className="error-message">{error}</p>}
          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? t('resetPasswordPage.resettingButton') : t('resetPasswordPage.resetButton')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
